import { Injectable } from "@angular/core";
import { HttpClient, HttpParams, HttpErrorResponse } from "@angular/common/http";
import { Subject, Observable, of, from } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { Router } from "@angular/router";

import { environment } from "src/environments/environment";

const BACKEND_URL = environment.apiUrl + "/branches/";
const EXPRESS_SERVER_URL = "https://img-express.eazix.io";

function getAuthTokenFromStorage(): string | null {
  const token = localStorage.getItem("token");
  if (token == null || token === "" || token === "undefined" || token === "null") {
    return null;
  }
  return token;
}

@Injectable({
  providedIn: "root"
})

export class BranchesService {
  private branches: any[] = [];
  private branchesUpdated = new Subject<{ branches: any[], branchCount: number }>();

  constructor(private http: HttpClient, private router: Router) { }

  getBranches(branchesPerPage: number, currentPage: number) {
    const queryParams = `?pagesize=${branchesPerPage}&page=${currentPage}`;
    this.http
      .get<{ message: string; branches: any, maxBranches: number }>(BACKEND_URL + queryParams)
      .pipe(
        map(branchData => {
          return {
            branches: branchData.branches.map(branch => {
              return {
                id: branch._id,
                branches: branch,
              };
            }),
            maxBranches: branchData.maxBranches
          };
        })
      )
      .subscribe(transformedBranchData => {
        this.branches = transformedBranchData.branches;
        this.branchesUpdated.next({
          branches: [...this.branches],
          branchCount: transformedBranchData.maxBranches
        });
      });
  }

  getAllBranches(): Observable<any[]> {
    return this.http
      .get<{ message: string; branches: any[] }>(BACKEND_URL + 'allbranches')
      .pipe(
        map(branchData => {
          return [...branchData.branches];
        })
      );
  }

  getBranchScanningStatus(
    printerId: string,
    userId: string
  ): Observable<any> {
    return this.http.get<{
      message: string,
      scanningStatus: string,
    }>(BACKEND_URL + 'branchscanningstatus/' + printerId + '/' + userId);
  }

  getQueData(printingService: string, branchIdArray: string[]) {
    return this.http.get<{
      message: string,
      fetchedBranches: any,
    }>(BACKEND_URL + 'quedata/' + printingService + '/' + branchIdArray)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 0 && error.statusText === 'Unknown Error') {
            console.error('Ignored error:', error);
            return of(null);
          }
          console.error("error thrown:", error);
          return of(null);
        })
      );
  }

  getBranchUpdateListener() {
    return this.branchesUpdated.asObservable();
  }

  getBranchById(service: string, branch: string): Observable<any> {
    return this.http
      .get<any>(BACKEND_URL + 'branchbyid/' + service + "/" + branch)
      .pipe(
        catchError(error => {
          console.error('Error fetching branch:', error);
          throw error;
        })
      );
  }

  getExpressPrintingCode(printerId: string): Observable<{ unique: number, code: number }> {
    return this.http
      .get<{ message: string, unique: number, code: number }>(BACKEND_URL + 'express-printing-code/' + printerId)
      .pipe(
        map(response => ({
          unique: response.unique,
          code: response.code
        })),
        catchError(error => {
          console.error('Error fetching express printing code:', error);
          throw error;
        })
      );
  }

  updateLockCode(service: string, id: string, lockCode: string): Observable<{ message: string, lockCode: string }> {
    return this.http
      .put<{ message: string, lockCode: string }>(BACKEND_URL + 'updatelockcode/' + service + '/' + id, { lockCode })
      .pipe(
        catchError(error => {
          console.error('Error updating lock code:', error);
          throw error;
        })
      );
  }

  getBranchByName(service: string, branch: string): Observable<any> {
    return this.http
      .get<any>(BACKEND_URL + 'branchbyname/' + service + "/" + branch)
      .pipe(
        catchError(error => {
          console.error('Error fetching branch:', error);
          throw error;
        })
      );
  }

  getBranch(id: string) {
    return this.http.get<{
      _id: string,
      serial_name: string,
      // ADD EDIT BRANCH PROPERTIES HERE 1:
      close: boolean,
      close_msg: string,
      domain: string,
      downgraded: boolean,
      email: string,
      hide: boolean,
      hotjarID: string,
      inform_slack_of_new_orders: boolean,
      location: string,
      name: string,
      short_name: string,
      slack_url: string,
      sort: number,
      printers: any[],
    }>(
      BACKEND_URL + id
    );
  }

  onCheckBranchStatus(service: string, branch: string) {
    return this.http.get<{
      status: any,
    }>(
      BACKEND_URL + 'checkbranchstatus/' + service + '/' + branch
    );
  }

  getBranchByUnique(unique: string) {
    return this.http.get<{
      branch: any,
    }>(
      BACKEND_URL + 'unique/' + unique
    );
  }

  addBranch(
    serial_name: string,
    // ADD EDIT BRANCH PROPERTIES HERE:
    close: boolean,
    close_msg: string,
    domain: string,
    downgraded: boolean,
    email: string,
    hide: boolean,
    hotjarID: string,
    inform_slack_of_new_orders: boolean,
    location: string,
    name: string,
    short_name: string,
    slack_url: string,
    sort: number,
    papers: any[],
  ) {
    const branchData = new FormData();
    branchData.append("serial_name", serial_name);
    // ADD EDIT BRANCH PROPERTIES HERE:
    branchData.append("close", String(close));
    branchData.append("close_msg", close_msg);
    branchData.append("domain", domain);
    branchData.append("downgraded", String(downgraded));
    branchData.append("email", email);
    branchData.append("hide", String(hide));
    branchData.append("hotjarID", hotjarID);
    branchData.append("inform_slack_of_new_orders", String(inform_slack_of_new_orders));
    branchData.append("location", location);
    branchData.append("name", name);
    branchData.append("short_name", short_name);
    branchData.append("slack_url", slack_url);
    branchData.append("sort", String(sort));
    branchData.append("papers", JSON.stringify(papers));
    this.http
      .post<{ message: string; branch: any }>(
        BACKEND_URL,
        branchData
      )
      .subscribe(responseData => {
        this.router.navigate(["/branchlist"]);
      });
  }

  updateBranch(
    id: string,
    serial_name: string,
    // ADD EDIT BRANCH PROPERTIES HERE:
    close: boolean,
    close_msg: string,
    domain: string,
    downgraded: boolean,
    email: string,
    hide: boolean,
    hotjarID: string,
    inform_slack_of_new_orders: boolean,
    location: string,
    name: string,
    short_name: string,
    slack_url: string,
    sort: number,
    papers: any[],
  ) {
    let branchData: any | FormData;
    // if (typeof image === "object") {
    branchData = new FormData();
    branchData.append("id", id);
    branchData.append("serial_name", serial_name);
    // ADD EDIT BRANCH PROPERTIES HERE:
    branchData.append("close", String(close));
    branchData.append("close_msg", close_msg);
    branchData.append("domain", domain);
    branchData.append("downgraded", String(downgraded));
    branchData.append("email", email);
    branchData.append("hide", String(hide));
    branchData.append("hotjarID", hotjarID);
    branchData.append("inform_slack_of_new_orders", String(inform_slack_of_new_orders));
    branchData.append("location", location);
    branchData.append("name", name);
    branchData.append("short_name", short_name);
    branchData.append("slack_url", slack_url);
    branchData.append("sort", String(sort));
    branchData.append("papers", JSON.stringify(papers));
    this.http
      .put(BACKEND_URL + id, branchData)
      .subscribe(response => {
        this.router.navigate(["/branchlist"]);
      });
  }

  deleteBranch(branchId: string) {
    return this.http
      .delete(BACKEND_URL + branchId)
  }

  onOpenOrCloseQueue(id: string, isOpen: boolean) {
    const branchData = { isOpen: isOpen };
    return this.http.put(BACKEND_URL + 'openorclosequeue/' + id, branchData);
  }

  /**
   * פלוטר במוניטור בלבד – הבאק מצפה ל-body עם isOn בלבד.
   * PUT /api/branches/openorcloseslack/:id  Body: { "isOn": boolean }
   * הבאק עושה: inform_slack_of_new_orders = isOn ? false : true (הפיכה).
   */
  openOrCloseSlackPlotter(branchId: string, isOn: boolean) {
    const body = { isOn };
    const url = BACKEND_URL + 'openorcloseslack/' + branchId;
    return this.http.put<{ message: string; branch: any }>(url, body);
  }

  onOpenOrCloseSlack(id: string, isOn: boolean) {
    const branchData = { isOn, inform_slack_of_new_orders: isOn };
    return this.http.put(BACKEND_URL + 'openorcloseslack/' + id, branchData);
  }

  onOpenOrCloseSlackExpress(printerId: string, isOn: boolean) {
    const printerData = { isOn: isOn };
    return this.http.put(BACKEND_URL + 'openorcloseslackexpress/' + printerId, printerData);
  }

  checkExpressPrinterStatus(printerId: string, email?: string, phone?: string | number) {
    // Fire and forget - no response expected
    this.http.post(BACKEND_URL + 'checkexpressprinterstatus/' + printerId, { email, phone }).subscribe(
      () => {},
      () => {}
    );
  }

  closeBranch(
    serice: string,
    branch: string,
    close_msg: string
  ) {
    const branchData = { close_msg: { close_msg } };
    return this.http.put(BACKEND_URL + 'closebranch/' + serice + '/' + branch, branchData);
  }

  openBranch(
    serice: string,
    branch: string
  ) {
    return this.http.put(BACKEND_URL + 'openbranch/' + serice + '/' + branch, null);
  }

  onUpdateInventory(
    service: string,
    branchId: string,
    inventory: any
  ) {
    const branchData = { inventory: inventory };
    console.log("onUpdateInventory branchData", branchData);
    return this.http.put(BACKEND_URL + 'updateinventory/' + service + '/' + branchId, branchData);
  }

  onReplaceBm(
    printingService: string,
    branchId: string,
    userId: string
  ) {
    const branchData = { userId: userId };
    return this.http.put(BACKEND_URL + 'replacebm/' + printingService + '/' + branchId, branchData);
  }

  onRemoveSt(
    printingService: string,
    branchId: string,
    userId: string
  ) {
    const branchData = { userId: userId };
    return this.http.put(BACKEND_URL + 'removest/' + printingService + '/' + branchId, branchData);
  }

  onAddSt(
    printingService: string,
    branchId: string,
    userId: string
  ) {
    const branchData = { userId: userId };
    return this.http.put(BACKEND_URL + 'addst/' + printingService + '/' + branchId, branchData);
  }

  onRemovePrinterHelper(
    printingService: string,
    branchId: string,
    userId: string
  ) {
    const branchData = { userId: userId };
    return this.http.put(BACKEND_URL + 'removeprinterhelper/' + printingService + '/' + branchId, branchData);
  }

  onAddPrinterHelper(
    printingService: string,
    branchId: string,
    userId: string
  ) {
    const branchData = { userId: userId };
    return this.http.put(BACKEND_URL + 'addprinterhelper/' + printingService + '/' + branchId, branchData);
  }

  // onReplaceConsumable(
  //   printingService: string,
  //   branchId: string,
  //   type: string,
  //   consumable: string
  // ) {
  //   const branchData = {
  //     type: type,
  //     consumable: consumable
  //   };
  //   return this.http.put(BACKEND_URL + '/replaceconsumable/' + printingService + '/' + branchId, branchData);
  // }

  // [plotter]
  onCancelOrderFromQueue(
    action: string,
    orderId: string,
    branchSerialName: string
  ): Observable<any> {
    return this.http.put(`${BACKEND_URL}cancelorderfromqueue/${action}/${branchSerialName}/${orderId}`, null);
  }

  onPromoteOrderInQueue(
    orderId: string,
    branchSerialName: string
  ): Observable<any> {
    return this.http.put(`${BACKEND_URL}promoteorderinqueue/${branchSerialName}/${orderId}`, null);
  }

  onMoveOrderToPosition(
    orderId: string,
    branchSerialName: string,
    targetIndex: number
  ): Observable<any> {
    return this.http.put(`${BACKEND_URL}moveordertoposition/${branchSerialName}/${orderId}/${targetIndex}`, null);
  }

  onMoveOrderUp(
    orderId: string,
    branchSerialName: string
  ): Observable<any> {
    return this.http.put(`${BACKEND_URL}moveorderup/${branchSerialName}/${orderId}`, null);
  }

  onMoveOrderDown(
    orderId: string,
    branchSerialName: string
  ): Observable<any> {
    return this.http.put(`${BACKEND_URL}moveorderdown/${branchSerialName}/${orderId}`, null);
  }

  // [express]
  releaseQueueExpress(printerID: string): Observable<any> {
    console.log("printerID", printerID);
    return this.http.post(`${BACKEND_URL}releasequeueexpress`, { printerID: printerID });
  }

  resetExpressSpooler(unique: string): Observable<void> {
    const url = `${EXPRESS_SERVER_URL}/api/printers/spooler/reset/${encodeURIComponent(unique)}`;
    const token = getAuthTokenFromStorage();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return from(
      fetch(url, { method: "POST", headers }).then(async (res) => {
        let data: { success?: boolean } = {};
        try {
          data = await res.json();
        } catch {
          // response may have no JSON body
        }

        if (res.status === 200 && data.success === true) {
          return;
        }
        if (res.status === 401) {
          throw { message: "AUTH_FAILED_TOKEN_INCORECT" };
        }
        if (res.status === 422 || data.success === false) {
          throw { message: "RELEASE_QUEUE_AGGRESSIVE_PRINTER_NOT_FOUND" };
        }
        throw { message: "RELEASE_QUEUE_AGGRESSIVE_FAILED" };
      })
    );
  }

  releaseStuckExpressOrder(printerID: string, userEmail: string): Observable<any> {
    console.log("printerID", printerID, "userEmail", userEmail);
    return this.http.post(`${BACKEND_URL}releasestuckexpressorder`, { printerID: printerID, userEmail: userEmail });
  }

  /** קוד הדפסה אקספרס (מספר) — כמו calculatePrintingCode ב-order-summary / printing-table. */
  buildExpressPrintingCode(printerNum: number, randomDigits: number): number {
    let code = '00000';
    if (printerNum && randomDigits) {
      code =
        printerNum.toString().charAt(0) +
        randomDigits.toString().charAt(0) +
        printerNum.toString().charAt(1) +
        randomDigits.toString().charAt(1) +
        printerNum.toString().charAt(2);
    }
    return parseInt(code, 10);
  }

  /**
   * אקספרס: שליחה להדפסה של ההזמנה המשוכפלת (partial resend).
   * ההזמנה המשוכפלת מפנה לקובץ מעובד שכבר קיים ב-S3 (pdfPath), ולכן זו למעשה
   * הדפסה חוזרת (rePrint) של קובץ מוכן — לא הדפסה ראשונה שדורשת עיבוד מקומי.
   * כפה ראסטר מטופל ב-IMG-Express במסלול ה-rePrint (rasterizeOrderFilesForReprint).
   */
  triggerExpressOrderPrint(
    serverAddress: string,
    orderId: string,
    printingCode: number | string,
    forceRasterize: boolean = false
  ): Observable<any> {
    const baseUrl = (serverAddress || EXPRESS_SERVER_URL).replace(/\/$/, '');
    const code =
      typeof printingCode === 'number'
        ? printingCode
        : parseInt(String(printingCode), 10);
    const body: {
      orderID: string;
      code: number;
      rePrint: boolean;
      forceRasterize?: boolean;
    } = {
      orderID: orderId,
      code,
      rePrint: true,
    };
    if (forceRasterize === true) {
      body.forceRasterize = true;
    }
    return this.http.post(`${baseUrl}/api/orders/print`, body);
  }

  resendPartialOrder(
    orderId: string,
    service: 'plotter' | 'express',
    selections: { fileId: string; fileIncluded: boolean; pagesIncluded: number[] }[],
    forceRasterize: boolean = false
  ): Observable<any> {
    const body: {
      orderId: string;
      service: 'plotter' | 'express';
      selections: { fileId: string; fileIncluded: boolean; pagesIncluded: number[] }[];
      forceRasterize?: boolean;
    } = {
      orderId,
      service,
      selections,
    };
    if (forceRasterize === true) {
      body.forceRasterize = true;
    }
    const url = `${BACKEND_URL}resendpartialorder`;
    return this.http.post(url, body);
  }

  onCancelOrder(
    servece: string,
    action: string,
    orderId: string,
  ): Observable<any> {
    return this.http.put(`${BACKEND_URL}cancelorder/${servece}/${action}/${orderId}`, null);
  }
  // ...

  // =======================================
  // Added queue movement functions for orders

  // Update message to customer
  updateMessageToCustomer(
    service: string,
    branchId: string,
    title?: string,
    content?: string,
    buttonText?: string,
    isOn?: boolean
  ): Observable<any> {
    const body: any = {};
    if (title !== undefined) body.title = title;
    if (content !== undefined) body.content = content;
    if (buttonText !== undefined) body.buttonText = buttonText;
    if (isOn !== undefined) body.isOn = isOn;
    
    return this.http.put(`${BACKEND_URL}updatemessagetocustomer/${service}/${branchId}`, body);
  }

  updatePaperBlockStatus(
    service: string,
    branchId: string,
    paperKey: string,
    isPreventedForUse: boolean
  ): Observable<any> {
    const body = {
      paperKey: paperKey,
      isPreventedForUse: isPreventedForUse
    };
    return this.http.put(`${BACKEND_URL}updatepaperblockstatus/${service}/${branchId}`, body);
  }

  requestUpdateWhenBranchOpens(
    printingService: string,
    branchId: string,
    userId: string
  ): Observable<{ message: string; code?: string }> {
    const body = {
      printingService: printingService,
      branchId: branchId,
      userId: userId
    };
    return this.http.post<{ message: string; code?: string }>(
      `${BACKEND_URL}request-update-when-opens`,
      body
    );
  }

  getExpressInternalQueue(printerId: string): Observable<{ success: boolean; data: any }> {
    return this.http.get<{ success: boolean; data: any }>(
      `${environment.apiUrl}/api/update/monitor/express/${printerId}/internal-queue`
    );
  }
}


